import { createClient, User, Session, AuthError } from '@supabase/supabase-js';
import type { CallRecord, CallRecordInsert, RealtimePayload } from '@/types';

// Environment variables - Production DB
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://gtfudxnvhjhsdbnzslap.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd0ZnVkeG52aGpoc2RibnpzbGFwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzAwMjE2NDMsImV4cCI6MjA4NTU5NzY0M30.ikeZb9jj6Y5bFp_ZsmlPteBbSYkV-fIq4puK3KQatA4';

// Create Supabase client with explicit session persistence configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: {
      getItem: (key: string): string | null => {
        try {
          return localStorage.getItem(key);
        } catch {
          return null;
        }
      },
      setItem: (key: string, value: string): void => {
        try {
          localStorage.setItem(key, value);
        } catch (e) {
          console.error('Error saving to localStorage:', e);
        }
      },
      removeItem: (key: string): void => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.error('Error removing from localStorage:', e);
        }
      },
    },
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// Table names constant for easy reference
export const TABLES = {
  CALL_RECORDS: 'alsalama_call_records',
  PROFILES: 'profiles',
  FIELD_VISIBILITY_PROFILES: 'field_visibility_profiles',
  GLOBAL_SETTINGS: 'global_settings',
} as const;

// Visibility context type
export type VisibilityContext = 'table' | 'modal' | 'csv' | 'pdf';

// Field visibility config type
export type FieldVisibilityConfig = Record<string, Record<VisibilityContext, boolean>>;

// Field visibility profile type
export interface FieldVisibilityProfile {
  id: string;
  name: string;
  description: string | null;
  visibility_config: FieldVisibilityConfig;
  is_default: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ============ Authentication Functions ============

export async function signUp(
  email: string,
  password: string,
  options?: {
    fullName?: string;
    data?: Record<string, unknown>;
  }
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: options?.fullName,
          ...options?.data,
        },
      },
    });

    return { user: data.user, error };
  } catch (error) {
    console.error('Error signing up:', error);
    return { user: null, error: error as AuthError };
  }
}

export async function signIn(
  email: string,
  password: string
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { user: data.user, error };
  } catch (error) {
    console.error('Error signing in:', error);
    return { user: null, error: error as AuthError };
  }
}

export async function signInWithOtp(
  email: string
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
    });

    return { user: data.user, error };
  } catch (error) {
    console.error('Error signing in with OTP:', error);
    return { user: null, error: error as AuthError };
  }
}

export async function signInWithOAuth(
  provider: 'google' | 'github' | 'facebook'
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin,
      },
    });

    // OAuth responses don't have user property directly
    return { user: null, error };
  } catch (error) {
    console.error('Error signing in with OAuth:', error);
    return { user: null, error: error as AuthError };
  }
}

export async function signOut(): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signOut();
    return { error };
  } catch (error) {
    console.error('Error signing out:', error);
    return { error: error as AuthError };
  }
}

export async function getCurrentUser(): Promise<{ user: User | null }> {
  const { data } = await supabase.auth.getUser();
  return { user: data.user };
}

export async function getSession(): Promise<{ session: Session | null }> {
  const { data } = await supabase.auth.getSession();
  return { session: data.session };
}

export async function resetPassword(email: string): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  } catch (error) {
    console.error('Error resetting password:', error);
    return { error: error as AuthError };
  }
}

export async function updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  } catch (error) {
    console.error('Error updating password:', error);
    return { error: error as AuthError };
  }
}

export async function updateUser(
  data: {
    email?: string;
    password?: string;
    fullName?: string;
    phone?: string;
  }
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { data: updatedData, error } = await supabase.auth.updateUser({
      email: data.email,
      password: data.password,
      data: {
        full_name: data.fullName,
        phone: data.phone,
      },
    });

    return { user: updatedData.user, error };
  } catch (error) {
    console.error('Error updating user:', error);
    return { user: null, error: error as AuthError };
  }
}

export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
): () => void {
  const { data } = supabase.auth.onAuthStateChange(callback);
  return () => data.subscription.unsubscribe();
}

// ============ Generic CRUD Functions ============

export interface FetchOptions {
  page?: number;
  pageSize?: number;
  search?: string;
  searchColumns?: string[];
  from?: string;
  to?: string;
  filters?: Record<string, unknown>;
  orderBy?: string;
  orderDir?: 'asc' | 'desc';
}

export interface FetchResult<T> {
  data: T[] | null;
  error: Error | null;
  count?: number;
}

export async function fetchRecords<T extends Record<string, unknown>>(
  tableName: string,
  options?: FetchOptions
): Promise<FetchResult<T>> {
  try {
    let query = supabase.from(tableName).select('*', { count: 'exact' });

    // Apply date range filter
    if (options?.from) {
      query = query.gte('created_at', options.from);
    }
    if (options?.to) {
      query = query.lte('created_at', options.to);
    }

    // Apply search filter
    if (options?.search && options.search.trim()) {
      const searchTerm = `%${options.search.trim()}%`;
      const searchColumns = options.searchColumns || ['name'];

      if (searchColumns.length > 1) {
        const conditions = searchColumns.map(col => `${col}.ilike.${searchTerm}`).join(',');
        query = query.or(conditions);
      } else {
        query = query.ilike(searchColumns[0], searchTerm);
      }
    }

    // Apply additional filters
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          query = query.eq(key, value);
        }
      });
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy, { ascending: options.orderDir === 'asc' });
    } else {
      query = query.order('created_at', { ascending: false });
    }

    // Apply pagination
    if (options?.page !== undefined && options?.pageSize) {
      const from = options.page * options.pageSize;
      const to = from + options.pageSize - 1;
      query = query.range(from, to);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    // Convert bigint id to string for JSON serialization
    const serializedData = data?.map(record => {
      const newRecord = { ...record };
      if ('id' in newRecord && typeof newRecord.id === 'bigint') {
        newRecord.id = String(newRecord.id);
      }
      return newRecord;
    }) as T[];

    return { data: serializedData, error: null, count: count || undefined };
  } catch (error) {
    console.error(`Error fetching records from ${tableName}:`, error);
    return { data: null, error: error as Error };
  }
}

export async function getRecord<T extends Record<string, unknown>>(
  tableName: string,
  id: string
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    // Convert bigint id to string
    if (data && 'id' in data && typeof data.id === 'bigint') {
      data.id = String(data.id) as T['id'];
    }

    return { data, error: null };
  } catch (error) {
    console.error(`Error fetching record from ${tableName}:`, error);
    return { data: null, error: error as Error };
  }
}

export async function insertRecord<T extends Record<string, unknown>>(
  tableName: string,
  record: T
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .insert(record)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error(`Error inserting record into ${tableName}:`, error);
    return { data: null, error: error as Error };
  }
}

export async function updateRecord<T extends Record<string, unknown>>(
  tableName: string,
  id: string,
  updates: Partial<T>
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.from(tableName).update(updates).eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error(`Error updating record in ${tableName}:`, error);
    return { error: error as Error };
  }
}

export async function deleteRecord(
  tableName: string,
  id: string
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase.from(tableName).delete().eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error(`Error deleting record from ${tableName}:`, error);
    return { error: error as Error };
  }
}

export async function getRecordCount(
  tableName: string,
  options?: {
    from?: string;
    to?: string;
    search?: string;
    searchColumns?: string[];
    filters?: Record<string, unknown>;
  }
): Promise<{ count: number; error: Error | null }> {
  try {
    const isCallRecordsTable = tableName === TABLES.CALL_RECORDS;
    let query = supabase.from(tableName).select('*', { count: 'exact', head: true });

    // Apply date range filter
    if (options?.from) {
      query = query.gte('created_at', options.from);
    }
    if (options?.to) {
      query = query.lte('created_at', options.to);
    }
    
    // Apply search filter - only for alsalama_call_records table
    if (options?.search && options.search.trim() && isCallRecordsTable) {
      const searchTerm = `%${options.search.trim()}%`;
      const searchColumns = options.searchColumns || ['name'];

      if (searchColumns.length > 1) {
        const conditions = searchColumns.map(col => `${col}.ilike.${searchTerm}`).join(',');
        query = query.or(conditions);
      } else {
        query = query.ilike(searchColumns[0], searchTerm);
      }
    }

    // Apply filters - only for alsalama_call_records table
    if (options?.filters && isCallRecordsTable) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          query = query.eq(key, value);
        }
      });
    }

    const { count, error } = await query;

    if (error) {
      console.error(`Error getting count from ${tableName}:`, error);
      // Fallback: just count all records
      const fallbackQuery = supabase.from(tableName).select('*', { count: 'exact', head: true });
      const { count: fallbackCount } = await fallbackQuery;
      return { count: fallbackCount || 0, error: null };
    }

    return { count: count || 0, error: null };
  } catch (error) {
    console.error(`Error getting count from ${tableName}:`, error);
    return { count: 0, error: error as Error };
  }
}

// ============ Call Records Specific Functions ============

export async function fetchCallRecords(
  options?: {
    tableName?: string;
    page?: number;
    pageSize?: number;
    search?: string;
    from?: string;
    to?: string;
    category?: string | null;
    serviceType?: string | null;
    status?: boolean | null;
    urgency?: string | null;
    orderBy?: keyof CallRecord;
    orderDir?: 'asc' | 'desc';
  }
): Promise<{ data: CallRecord[] | null; error: Error | null }> {
  try {
    const tableName = options?.tableName || TABLES.CALL_RECORDS;
    
    // For non-alsalama_call_records tables, use a simpler query without specific column filters
    const isCallRecordsTable = tableName === TABLES.CALL_RECORDS;
    
    let query = supabase.from(tableName).select('*', { count: 'exact' });

    // Apply date range filter (only for alsalama_call_records or if created_at exists)
    if (options?.from) {
      query = query.gte('created_at', options.from);
    }
    if (options?.to) {
      query = query.lte('created_at', options.to);
    }

    // Apply search filter - only for alsalama_call_records table (has the right columns)
    if (options?.search && options.search.trim() && isCallRecordsTable) {
      const searchTerm = `%${options.search.trim()}%`;
      query = query.or(
        `name.ilike.${searchTerm},phone_number.ilike.${searchTerm},call_summary.ilike.${searchTerm},category.ilike.${searchTerm},service_type.ilike.${searchTerm},inquiry.ilike.${searchTerm},conversation_id.ilike.${searchTerm}`
      );
    }

    // Apply category filter - only for alsalama_call_records table
    if (options?.category && isCallRecordsTable) {
      query = query.eq('category', options.category);
    }

    // Apply service type filter - only for alsalama_call_records table
    if (options?.serviceType && isCallRecordsTable) {
      query = query.eq('service_type', options.serviceType);
    }

    // Apply status filter - only for alsalama_call_records table (or adapt for sale_status)
    if (options?.status !== null && options?.status !== undefined && isCallRecordsTable) {
      query = query.eq('status', options.status);
    }

    // Apply urgency filter - only for alsalama_call_records table
    if (options?.urgency && isCallRecordsTable) {
      query = query.eq('urgency', options.urgency);
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy, { ascending: options.orderDir === 'asc' });
    } else {
      query = query.order('created_at', { ascending: false });
    }

    // Apply pagination
    if (options?.page !== undefined && options?.pageSize) {
      const from = options.page * options.pageSize;
      const to = from + options.pageSize - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching records:', error);
      // Try a simpler query without filters
      const fallbackQuery = supabase.from(tableName).select('*', { count: 'exact' });
      const fallbackResult = await fallbackQuery.range(
        (options?.page || 0) * (options?.pageSize || 25),
        ((options?.page || 0) * (options?.pageSize || 25)) + (options?.pageSize || 25) - 1
      );
      
      if (fallbackResult.error) throw fallbackResult.error;
      
      const serializedData = fallbackResult.data?.map(record => ({
        ...record,
        id: String(record.id),
      })) as CallRecord[];
      
      return { data: serializedData, error: null };
    }

    // Convert bigint id to string for JSON serialization
    const serializedData = data?.map(record => ({
      ...record,
      id: String(record.id),
    })) as CallRecord[];

    return { data: serializedData, error: null };
  } catch (error) {
    console.error('Error fetching call records:', error);
    return { data: null, error: error as Error };
  }
}

export async function insertCallRecord(
  record: CallRecordInsert,
  tableName?: string
): Promise<{ data: CallRecord | null; error: Error | null }> {
  try {
    const table = tableName || TABLES.CALL_RECORDS;
    const { data, error } = await supabase
      .from(table)
      .insert(record)
      .select()
      .single();

    if (error) throw error;

    const serializedData = data ? {
      ...data,
      id: String(data.id),
    } as CallRecord : null;

    return { data: serializedData, error: null };
  } catch (error) {
    console.error('Error inserting call record:', error);
    return { data: null, error: error as Error };
  }
}

export async function updateCallRecordStatus(
  id: string,
  status: boolean,
  tableName?: string
): Promise<{ error: Error | null }> {
  try {
    const table = tableName || TABLES.CALL_RECORDS;
    const { error } = await supabase
      .from(table)
      .update({ status })
      .eq('id', id);

    if (error) {
      console.error('Supabase update error:', error);
      throw new Error(error.message);
    }

    return { error: null };
  } catch (error) {
    console.error('Error updating record status:', error);
    if (error instanceof Error) {
      return { error };
    }
    return { error: new Error(String(error)) };
  }
}

export async function deleteCallRecord(id: string, tableName?: string): Promise<{ error: Error | null }> {
  try {
    const table = tableName || TABLES.CALL_RECORDS;
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Supabase delete error:', error);
      throw new Error(error.message);
    }

    return { error: null };
  } catch (error) {
    console.error('Error deleting record:', error);
    return { error: error as Error };
  }
}

export async function getCallRecordCategories(tableName?: string): Promise<string[]> {
  try {
    const table = tableName || TABLES.CALL_RECORDS;
    const { data, error } = await supabase
      .from(table)
      .select('category')
      .not('category', 'is', null);

    if (error) throw error;

    const categories = [...new Set(data?.map(item => item.category).filter(Boolean) as string[])];
    return categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

export async function getCallRecordServiceTypes(tableName?: string): Promise<string[]> {
  try {
    const table = tableName || TABLES.CALL_RECORDS;
    const { data, error } = await supabase
      .from(table)
      .select('service_type')
      .not('service_type', 'is', null);

    if (error) throw error;

    const serviceTypes = [...new Set(data?.map(item => item.service_type).filter(Boolean) as string[])];
    return serviceTypes;
  } catch (error) {
    console.error('Error fetching service types:', error);
    return [];
  }
}

// ============ Realtime Subscriptions ============

// Generic function to subscribe to any table with proper type handling
export function subscribeToTable<T extends object>(
  tableName: string,
  callback: (payload: RealtimePayload<T>) => void
): () => void {
  const channelName = `${tableName}-changes-${Date.now()}`;

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: tableName,
      },
      (payload) => {
        const newRecord = payload.new as T | null;
        const oldRecord = payload.old as T | null;
        const serializedPayload = {
          ...payload,
          new: newRecord,
          old: oldRecord,
        };
        callback(serializedPayload as RealtimePayload<T>);
      }
    )
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR') {
        console.error(`Channel error for ${tableName}`);
      } else if (status === 'TIMED_OUT') {
        console.error(`Subscription timed out for ${tableName}`);
      }
    });

  return () => {
    supabase.removeChannel(channel);
  };
}

// Subscribe specifically to alsalama_call_records table
export function subscribeToCallRecords(
  callback: (payload: RealtimePayload<CallRecord>) => void
): () => void {
  return subscribeToTable<CallRecord>(TABLES.CALL_RECORDS, callback);
}

// ============ Utility Functions ============

export async function checkConnection(): Promise<boolean> {
  try {
    // Create an abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const { error } = await supabase
      .from(TABLES.CALL_RECORDS)
      .select('id')
      .limit(1)
      .abortSignal(controller.signal);
    
    clearTimeout(timeoutId);
    return !error;
  } catch {
    return false;
  }
}

// Helper function to format table name for display
function formatTableName(tableName: string): string {
  return tableName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

// Fetch all available table names from the database
export async function fetchAvailableTables(): Promise<{ name: string; label: string }[]> {
  try {
    // Try to query actual tables from database using get_tables function
    const { data, error } = await supabase.rpc('get_tables');

    // If RPC works and returns data, process it
    if (!error && data && Array.isArray(data) && data.length > 0) {
      const tables = data.map((row: any) => ({
        name: row.tablename,
        label: formatTableName(row.tablename),
      }));
      
      if (tables.length > 0) {
        return tables;
      }
    }

    // Try fallback: get available tables from global_settings
    const { data: settingsData, error: settingsError } = await supabase
      .from(TABLES.GLOBAL_SETTINGS)
      .select('setting_value')
      .eq('setting_key', 'available_tables')
      .maybeSingle();

    if (!settingsError && settingsData?.setting_value && Array.isArray(settingsData.setting_value)) {
      return settingsData.setting_value;
    }

    // Return default table if nothing works
    return [{ name: TABLES.CALL_RECORDS, label: 'Call Records' }];
  } catch (error) {
    console.error('Error fetching available tables:', error);
    // Return default table if fetch fails
    return [{ name: TABLES.CALL_RECORDS, label: 'Call Records' }];
  }
}

// Fetch columns for a specific table
export async function fetchTableColumns(tableName: string): Promise<string[]> {
  try {
    // Directly query the table to get columns from returned data (more reliable than RPC)
    const { data: sampleData, error: sampleError } = await supabase
      .from(tableName)
      .select('*')
      .limit(1);

    if (!sampleError && sampleData && sampleData.length > 0) {
      return Object.keys(sampleData[0]);
    }

    // Return default columns for alsalama_call_records
    if (tableName === TABLES.CALL_RECORDS) {
      return ['created_at', 'name', 'phone_number', 'category', 'service_type', 
              'inquiry', 'call_duration_sec', 'urgency', 'status', 'notes', 
              'call_summary', 'conversation_id', 'audio_uri'];
    }

    return ['id', 'created_at'];
  } catch (error) {
    console.error('Error fetching table columns:', error);
    // Return default columns
    if (tableName === TABLES.CALL_RECORDS) {
      return ['created_at', 'name', 'phone_number', 'category', 'service_type', 
              'inquiry', 'call_duration_sec', 'urgency', 'status', 'notes', 
              'call_summary', 'conversation_id', 'audio_uri'];
    }
    return ['id', 'created_at'];
  }
}

// Save available tables to global_settings
export async function saveAvailableTables(
  tables: { name: string; label: string }[]
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase
      .from(TABLES.GLOBAL_SETTINGS)
      .upsert({
        setting_key: 'available_tables',
        setting_value: tables,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'setting_key' });

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error saving available tables:', error);
    return { error: error as Error };
  }
}

export async function executeRawQuery<T>(
  query: string
): Promise<{ data: T[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase.rpc('exec_sql', { query });

    if (error) throw error;

    return { data: data as T[], error: null };
  } catch (error) {
    console.error('Error executing raw query:', error);
    return { data: null, error: error as Error };
  }
}


export async function updateCallRecord(
  id: string,
  updates: Partial<{
    name: string | null;
    phone_number: string | null;
    call_duration_sec: number | null;
    category: string | null;
    service_type: string | null;
    inquiry: string | null;
    status: boolean;
    urgency: string;
    notes: string | null;
    call_summary: string | null;
  }>
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase
      .from(TABLES.CALL_RECORDS)
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Supabase update error:', error);
      throw new Error(error.message);
    }

    return { error: null };
  } catch (error) {
    console.error('Error updating record:', error);
    if (error instanceof Error) {
      return { error };
    }
    return { error: new Error(String(error)) };
  }
}


// ============ Field Visibility Profile Functions ============

export async function fetchFieldVisibilityProfiles(): Promise<{ data: FieldVisibilityProfile[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from(TABLES.FIELD_VISIBILITY_PROFILES)
      .select('*')
      .order('name', { ascending: true });

    if (error) throw error;

    return { data: data as FieldVisibilityProfile[], error: null };
  } catch (error) {
    console.error('Error fetching field visibility profiles:', error);
    return { data: null, error: error as Error };
  }
}

export async function getFieldVisibilityProfile(id: string): Promise<{ data: FieldVisibilityProfile | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from(TABLES.FIELD_VISIBILITY_PROFILES)
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    return { data: data as FieldVisibilityProfile, error: null };
  } catch (error) {
    console.error('Error fetching field visibility profile:', error);
    return { data: null, error: error as Error };
  }
}

export async function getDefaultFieldVisibilityProfile(): Promise<{ data: FieldVisibilityProfile | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from(TABLES.FIELD_VISIBILITY_PROFILES)
      .select('*')
      .eq('is_default', true)
      .limit(1)
      .single();

    if (error) throw error;

    return { data: data as FieldVisibilityProfile, error: null };
  } catch (error) {
    console.error('Error fetching default field visibility profile:', error);
    return { data: null, error: error as Error };
  }
}

export async function createFieldVisibilityProfile(
  profile: {
    name: string;
    description?: string;
    visibility_config: FieldVisibilityConfig;
    is_default?: boolean;
  }
): Promise<{ data: FieldVisibilityProfile | null; error: Error | null }> {
  try {
    // If this is set as default, unset other defaults
    if (profile.is_default) {
      await supabase
        .from(TABLES.FIELD_VISIBILITY_PROFILES)
        .update({ is_default: false })
        .eq('is_default', true);
    }

    const { data, error } = await supabase
      .from(TABLES.FIELD_VISIBILITY_PROFILES)
      .insert(profile)
      .select()
      .single();

    if (error) throw error;

    return { data: data as FieldVisibilityProfile, error: null };
  } catch (error) {
    console.error('Error creating field visibility profile:', error);
    return { data: null, error: error as Error };
  }
}

export async function updateFieldVisibilityProfile(
  id: string,
  updates: {
    name?: string;
    description?: string;
    visibility_config?: FieldVisibilityConfig;
    is_default?: boolean;
  }
): Promise<{ error: Error | null }> {
  try {
    // If setting as default, unset other defaults first
    if (updates.is_default) {
      await supabase
        .from(TABLES.FIELD_VISIBILITY_PROFILES)
        .update({ is_default: false })
        .eq('is_default', true)
        .neq('id', id);
    }

    const { error } = await supabase
      .from(TABLES.FIELD_VISIBILITY_PROFILES)
      .update(updates)
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error updating field visibility profile:', error);
    return { error: error as Error };
  }
}

export async function deleteFieldVisibilityProfile(id: string): Promise<{ error: Error | null }> {
  try {
    // Check if this is the last profile
    const { data: profiles, error: countError } = await supabase
      .from(TABLES.FIELD_VISIBILITY_PROFILES)
      .select('id', { count: 'exact', head: true });

    if (countError) throw countError;
    if (profiles && profiles.length <= 1) {
      return { error: new Error('Cannot delete the last profile') };
    }

    // If this profile is default, make another one default
    const { data: profile } = await supabase
      .from(TABLES.FIELD_VISIBILITY_PROFILES)
      .select('is_default')
      .eq('id', id)
      .single();

    if (profile?.is_default) {
      await supabase
        .from(TABLES.FIELD_VISIBILITY_PROFILES)
        .update({ is_default: true })
        .eq('is_default', false)
        .neq('id', id)
        .limit(1);
    }

    // Unlink any users using this profile
    await supabase
      .from(TABLES.PROFILES)
      .update({ field_visibility_profile_id: null })
      .eq('field_visibility_profile_id', id);

    const { error } = await supabase
      .from(TABLES.FIELD_VISIBILITY_PROFILES)
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error deleting field visibility profile:', error);
    return { error: error as Error };
  }
}

export async function assignFieldVisibilityProfile(
  userId: string,
  profileId: string | null
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase
      .from(TABLES.PROFILES)
      .update({ field_visibility_profile_id: profileId })
      .eq('id', userId);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error assigning field visibility profile:', error);
    return { error: error as Error };
  }
}

export async function getUserFieldVisibilityConfig(userId: string): Promise<{ data: FieldVisibilityConfig | null; error: Error | null }> {
  try {
    // First get user's profile to see if they have a profile assigned
    const { data: profile, error: profileError } = await supabase
      .from(TABLES.PROFILES)
      .select('field_visibility_profile_id')
      .eq('id', userId)
      .single();

    if (profileError) throw profileError;

    let visibilityConfig: FieldVisibilityConfig;

    if (profile?.field_visibility_profile_id) {
      // Get the assigned profile's config
      const { data: profileData, error: configError } = await supabase
        .from(TABLES.FIELD_VISIBILITY_PROFILES)
        .select('visibility_config')
        .eq('id', profile.field_visibility_profile_id)
        .single();

      if (configError) throw configError;
      visibilityConfig = profileData.visibility_config;
    } else {
      // Get default profile
      const { data: defaultProfile, error: defaultError } = await supabase
        .from(TABLES.FIELD_VISIBILITY_PROFILES)
        .select('visibility_config')
        .eq('is_default', true)
        .limit(1)
        .single();

      if (defaultError) throw defaultError;
      visibilityConfig = defaultProfile.visibility_config;
    }

    return { data: visibilityConfig, error: null };
  } catch (error) {
    console.error('Error getting user field visibility config:', error);
    return { data: null, error: error as Error };
  }
}


// ============ Global Settings Functions ============

export async function getGlobalFieldVisibilityConfig(): Promise<{ data: FieldVisibilityConfig | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from(TABLES.GLOBAL_SETTINGS)
      .select('setting_value')
      .eq('setting_key', 'field_visibility')
      .single();

    if (error) throw error;

    return { data: data.setting_value as FieldVisibilityConfig, error: null };
  } catch (error) {
    console.error('Error getting global field visibility config:', error);
    return { data: null, error: error as Error };
  }
}

// Get field visibility config for a specific table
export async function getTableFieldVisibilityConfig(
  tableName: string
): Promise<{ data: FieldVisibilityConfig | null; error: Error | null }> {
  try {
    const settingKey = `field_visibility_${tableName}`;
    const { data, error } = await supabase
      .from(TABLES.GLOBAL_SETTINGS)
      .select('setting_value')
      .eq('setting_key', settingKey)
      .limit(1);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    // Check if data exists
    if (!data || data.length === 0) {
      return { data: null, error: null };
    }

    return { data: data[0].setting_value as FieldVisibilityConfig, error: null };
  } catch (error: unknown) {
    // Handle AbortError (request cancelled) - not a critical error
    if (error && typeof error === 'object' && 'message' in error && 
        (error.message === 'AbortError: signal is aborted without reason' || 
         String(error.message).includes('aborted'))) {
      console.log('Request aborted, ignoring...');
      return { data: null, error: null };
    }
    
    // Handle case where no rows found (not an error)
    if (error && typeof error === 'object' && 'code' in error && error.code === 'PGRST116') {
      return { data: null, error: null };
    }
    console.error(`Error getting field visibility config for table ${tableName}:`, error);
    return { data: null, error: error as Error };
  }
}

export async function updateGlobalFieldVisibilityConfig(
  config: FieldVisibilityConfig,
  updatedBy?: string
): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase
      .from(TABLES.GLOBAL_SETTINGS)
      .update({ 
        setting_value: config,
        updated_by: updatedBy || null,
      })
      .eq('setting_key', 'field_visibility');

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error updating global field visibility config:', error);
    return { error: error as Error };
  }
}

// Update field visibility config for a specific table
export async function updateTableFieldVisibilityConfig(
  tableName: string,
  config: FieldVisibilityConfig,
  updatedBy?: string
): Promise<{ error: Error | null }> {
  try {
    const settingKey = `field_visibility_${tableName}`;
    
    // First check if the setting exists
    const { data: existing, error: checkError } = await supabase
      .from(TABLES.GLOBAL_SETTINGS)
      .select('id')
      .eq('setting_key', settingKey)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      throw checkError;
    }

    if (existing) {
      // Update existing setting
      const { error } = await supabase
        .from(TABLES.GLOBAL_SETTINGS)
        .update({ 
          setting_value: config,
          updated_by: updatedBy || null,
          updated_at: new Date().toISOString(),
        })
        .eq('setting_key', settingKey);

      if (error) throw error;
    } else {
      // Insert new setting
      const { error } = await supabase
        .from(TABLES.GLOBAL_SETTINGS)
        .insert({ 
          setting_key: settingKey,
          setting_value: config,
          updated_by: updatedBy || null,
        });

      if (error) throw error;
    }

    return { error: null };
  } catch (error) {
    console.error(`Error updating field visibility config for table ${tableName}:`, error);
    return { error: error as Error };
  }
}
